import {
  Badge,
  Button as AntButton,
  Checkbox,
  Collapse,
  DatePicker,
  Drawer,
  Empty,
  Input,
  Radio,
  Select,
  Slider,
  Spin,
  Tag,
  message,
  Modal,
  Tooltip
} from 'antd'

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ExportStatusPanel from 'components/ExportStatusPanel'
import { LeftOutlined, RightOutlined, DoubleLeftOutlined, DoubleRightOutlined, EyeOutlined } from '@ant-design/icons';

import Button from 'components/Button/index'
import ExportButton from 'components/ExportButton/ExportButton.js'
import Pagination from 'components/Pagination'
import TableSearchFilter from 'components/UIComponents/TableSearchFilter'
import { StyledTable } from 'components/TableStyles/TableStyles'
import CandidateDetailsView from 'components/CandidateDetailsView'

import {
  RowNumberData,
  RowNumberEmailData,
} from 'components/TableStyles/TableContentStyles/TableContentStyles'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import candidateRequest from 'utils/candidateRequest'
import {
  EmptyWrapper,
  MenuFlex,
  NavWrapper,
  OverlayFlex,
  SectionWrapper,
  StyledLi,
  StyledP,
  TopHeader,
  TopHeading,
} from 'modules/Corporates/Style/style'

const PAGE_SIZE = 10

const CandidateMetrics = () => {
  const [loading, setLoading] = useState(false)
  const [ingestLoading, setIngestLoading] = useState(false)
  const [normalizationLoading, setNormalizationLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)

  // Exports are built by the worker and delivered by email — see handleDownload.
  const currentUserId = useSelector(state => state.auth?.userId) || null
  const currentUserEmail = useSelector(state => state.auth?.userData?.email) || ''
  const [exportStatus, setExportStatus] = useState(null)
  const pollRef = useRef(null)

  // Polling stops on `notified`, not on status === 'done': the file existing is
  // not the same as the requester having it.
  const startPolling = jobId => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        // export_jobs is one queue shared with the live-candidates export, so
        // job ids are global and this status endpoint serves both.
        const resp = await candidateRequest.get(
          `/api/student-metrics/download/status/${jobId}`
        )
        const { status, error, notified, notify_email: notifyEmail } = resp.data
        setExportStatus(prev =>
          prev ? { ...prev, status, notified, notifyEmail, errorMessage: error || null } : prev
        )
        if (notified || status === 'failed' || status === 'expired') {
          clearInterval(pollRef.current)
        }
      } catch { /* keep polling */ }
    }, 4000)
  }

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current)
  }, [])
  const [page, setPage] = useState(0) // admin-react uses 0-based internally
  const [searchValue, setSearchValue] = useState('')
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [viewCandidateDrawer, setViewCandidateDrawer] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [cvValidationLog, setCvValidationLog] = useState(null)
  const [normalizationLogs, setNormalizationLogs] = useState([])
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [filters, setFilters] = useState({
    dateRange: null,
    keywordSearchMode: 'profile',
    genders: [],
    experience: [0, 22],
    locations: [],
    degrees: [],
    departments: [],
    yearsOfPassing: [],
    marks: [0, 100],
    assessment: [0, 100],
    noticePeriods: [],
    statuses: [],
  })

  const [clearLoading, setClearLoading] = useState(false);

  const handleClearAll = () => {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'This will permanently clear all the candidate data from the list',
      okText: 'Yes, Clear All',
      okType: 'primary',
      cancelText: 'Cancel',

      onOk: async () => {
        try {
          setClearLoading(true);

          await candidateRequest.delete('/api/candidates/clear-all');

          message.success('All candidate data cleared');

          // refresh table
          await fetchCandidates();

        } catch (e) {
          message.error('Failed to clear data');
        } finally {
          setClearLoading(false);
        }
      },
    });
  };

  const sampleRows = useMemo(
    () => [],
    []
  )

  const fetchCandidates = useCallback(async () => {
    setLoading(true)

    try {
      const resp = await candidateRequest.get('/api/candidates', {
        params: {
          search: searchValue || undefined,
          page: page + 1,
          limit: PAGE_SIZE,
        },
      })

      setData(resp?.data?.data || [])
      setTotal(resp?.data?.total || 0)
    } catch (e) {
      message.error('Failed to load candidates')
      setData([])
      setTotal(0)
    } finally {
      // ❌ current
      // setLoading(false)

      // ✅ FIX: add delay
      setTimeout(() => {
        setLoading(false)
      }, 500) // adjust (500–1000ms recommended)
    }
  }, [page, searchValue])

  useEffect(() => {
    fetchCandidates()
  }, [fetchCandidates])

  useEffect(() => {
    setPage(0)
  }, [searchValue])

  const tableRows = data?.length ? data : sampleRows

  const statusTagColor = (value) => {
    const s = String(value || '').toLowerCase()
    if (s.includes('shortlist')) return 'green'
    if (s.includes('reject')) return 'red'
    if (s.includes('maybe')) return 'gold'
    if (s.includes('pending')) return 'blue'
    return 'default'
  }

  const renderSkills = (skills) => {
    const arr = Array.isArray(skills) ? skills : []
    const top = arr.slice(0, 2)
    const remaining = arr.length - top.length

    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {top.map((s) => (
          <Tag key={s}>{s}</Tag>
        ))}
        {remaining > 0 && <Tag>+{remaining}</Tag>}
        {arr.length === 0 && <RowNumberData>-</RowNumberData>}
      </div>
    )
  }

  const NormalizationLogs = ({ logs = [] }) => {

    const infoLogs = logs.filter(l => l.log_level === "INFO")
    const warningLogs = logs.filter(l => l.log_level === "WARNING")

    return (
      <Tabs defaultActiveKey="info">

        {/* INFO TAB */}
        <Tabs.TabPane
          tab={`🧠 INFO (${infoLogs.length})`}
          key="info"
        >
          <LogCards logs={infoLogs} type="info" />
        </Tabs.TabPane>

        {/* WARNING TAB */}
        <Tabs.TabPane
          tab={`⚠ WARNING (${warningLogs.length})`}
          key="warning"
        >
          <LogCards logs={warningLogs} type="warning" />
        </Tabs.TabPane>

      </Tabs>
    )
  }

  const LogCards = ({ logs, type }) => {

    if (!logs.length) {
      return <Empty description="No logs available" />
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

        {logs.map((log) => {

          const isWarning = type === "warning"
          const borderColor = isWarning ? "#faad14" : "#52c41a"
          const bgColor = isWarning ? "#fffbe6" : "#f6ffed"

          return (
            <div
              key={log.id}
              style={{
                border: "1px solid #eee",
                borderLeft: `4px solid ${borderColor}`,
                borderRadius: 8,
                padding: 12,
                background: bgColor
              }}
            >

              {/* HEADER */}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ fontWeight: 600 }}>
                  {isWarning ? "⚠" : "✔"} {log.step_name}
                </div>

                <div style={{ fontSize: 12, color: "#888" }}>
                  {log.created_at
                    ? new Date(log.created_at).toLocaleString()
                    : "-"}
                </div>
              </div>

              {/* CONFIDENCE */}
              {log.confidence_score !== null && (
                <div style={{ marginTop: 6 }}>
                  <Tag color={isWarning ? "gold" : "green"}>
                    Confidence: {(log.confidence_score * 100).toFixed(1)}%
                  </Tag>
                </div>
              )}

              {/* MESSAGE */}
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: "#444",
                  whiteSpace: "pre-wrap"
                }}
              >
                {log.message}
              </div>

            </div>
          )
        })}

      </div>
    )
  }

  const excelDataset = useMemo(() => {
    const columns = [
      { title: 'Name', width: { wch: 30 } },
      { title: 'Email', width: { wch: 35 } },
      { title: 'Mobile', width: { wch: 20 } },
      { title: 'Applied Role', width: { wch: 25 } },
      { title: 'Experience (yrs)', width: { wch: 18 } },
      { title: 'Key Skills', width: { wch: 45 } },
      { title: 'Location', width: { wch: 18 } },
      { title: 'Education', width: { wch: 35 } },
      { title: 'Notice', width: { wch: 25 } },
      { title: 'Score', width: { wch: 10 } },
      { title: 'Status', width: { wch: 15 } },
      { title: 'Source File', width: { wch: 35 } },
      { title: 'Source Sheet', width: { wch: 25 } },
      { title: 'Created At', width: { wch: 25 } },
    ]

    const sheetData = (tableRows || []).map((c) => [
      { value: c?._id ?? '' },
      { value: c?.name ?? '' },
      { value: c?.mobile ?? '' },
      { value: c?.applied_role ?? c?.role ?? '' },
      { value: c?.experience_years ?? c?.experience ?? '' },
      { value: Array.isArray(c?.key_skills) ? c.key_skills.join(', ') : '' },
      { value: c?.location ?? '' },
      { value: c?.education ?? '' },
      { value: c?.notice_period ?? '' },
      { value: c?.assessment_score ?? '' },
      { value: c?.status ?? c?.normalization_status ?? '' },
      { value: c?.source_file ?? '' },
      { value: c?.source_sheet ?? '' },
      { value: c?.created_at ?? '' },
    ])

    return [
      {
        columns,
        data: sheetData,
      },
    ]
  }, [tableRows])
  const handleIngest = async () => {
    try {
      setIngestLoading(true)

      await candidateRequest.post('/api/candidates/ingest')

      // Poll until background ingest finishes
      await new Promise((resolve) => {
        const poll = setInterval(async () => {
          try {
            const status = await candidateRequest.get('/api/candidates/ingest/status')
            if (!status?.data?.running) {
              clearInterval(poll)
              resolve()
            }
          } catch {
            clearInterval(poll)
            resolve()
          }
        }, 3000)
      })

      message.success({
        content: 'Ingest completed successfully',
        key: 'ingest',
      })

      await fetchCandidates()
    } catch (e) {
      message.error({ content: 'Ingest failed', key: 'ingest' })
    } finally {
      setIngestLoading(false)
    }
  }

  const handleNormalize = async () => {
    try {
      setNormalizationLoading(true)
      message.loading({ content: 'Normalization started in background...', key: 'normalize', duration: 0 })

      const resp = await candidateRequest.post('/api/candidates/normalize')

      const isTestRun = resp?.data?.is_test_run
      message.success({
        content: isTestRun
          ? 'Test mode: processing only 1 pending record'
          : 'Normalization started successfully.',
        key: 'normalize',
        duration: 5,
      })

      // Refresh after a delay since it runs in background
      setTimeout(() => {
        fetchCandidates()
      }, 3000)
    } catch (e) {
      message.error({ content: 'Failed to start normalization', key: 'normalize' })
    } finally {
      setNormalizationLoading(false)
    }
  }

  const handleRefresh = async () => {
    message.loading({ content: 'Refreshing...', key: 'refresh', duration: 1 })
    await fetchCandidates()
  }
  const sampleApiJson = useMemo(
    () =>
      JSON.stringify(
        {
          items: [
            {
              _id: 123,
              name: 'John Doe',
              email: 'john@example.com',
              mobile: '9999999999',
              applied_role: 'ML Engineer',
              experience_years: 10,
              key_skills: ['Python', 'Machine Learning', 'NLP', 'SQL'],
              location: 'Pune',
              education: {
                degree: 'M.Tech',
                department: 'IISc',
                institute: 'Bangalore',
              },
              notice_period: 'More than 3 Months',
              assessment_score: 95,
              status: 'Shortlisted',
              normalization_status: 'pending',
              source_file: 'Hiring - Feb.xlsx',
              source_sheet: 'Sheet1',
              created_at: '2026-02-23T10:00:00+00:00',
            },
          ],
          total: 1,
          page: 1,
          page_size: 10,
        },
        null,
        2
      ),
    []
  )

  const [isExcel, setIsExcel] = useState('')
  const clickedExcel = (type) => setIsExcel(type)
  const [downloadModal, setDownloadModal] = useState(false)
  const [availableCols, setAvailableCols] = useState([])
  const [selectedCols, setSelectedCols] = useState([])
  const [columnsLoading, setColumnsLoading] = useState(false)

  const fetchColumns = async () => {
    try {
      setColumnsLoading(true)

      const resp = await candidateRequest.get('/api/candidates/columns')

      const cols = resp?.data?.data || []

      setAvailableCols(cols)
    } catch (e) {
      message.error('Failed to load columns')
    } finally {
      setColumnsLoading(false)
    }
  }

  useEffect(() => {
    if (downloadModal) fetchColumns()
  }, [downloadModal])

  const moveToSelected = (col) => {
    setAvailableCols(prev => prev.filter(c => c.field_name !== col.slug_name))
    setSelectedCols(prev => [...prev, col])
  }

  const moveToAvailable = (col) => {
    setSelectedCols(prev => prev.filter(c => c.field_name !== col.slug_name))
    setAvailableCols(prev => [...prev, col])
  }

  const moveAllToSelected = () => {
    setSelectedCols(prev => [...prev, ...availableCols])
    setAvailableCols([])
  }

  const moveAllToAvailable = () => {
    setAvailableCols(prev => [...prev, ...selectedCols])
    setSelectedCols([])
  }

  const [availableCheckboxes, setAvailableCheckboxes] = useState({})
  const [selectedCheckboxes, setSelectedCheckboxes] = useState({})

  const handleAvailableCheckboxChange = (slug, checked) => {
    setAvailableCheckboxes(prev => ({ ...prev, [slug]: checked }))
  }

  const handleSelectedCheckboxChange = (slug, checked) => {
    setSelectedCheckboxes(prev => ({ ...prev, [slug]: checked }))
  }

  const handleSelectAllAvailable = (checked) => {
    const newCheckboxes = {}
    availableCols.forEach(col => {
      newCheckboxes[col.slug_name] = checked
    })
    setAvailableCheckboxes(newCheckboxes)
  }

  const handleSelectAllSelected = (checked) => {
    const newCheckboxes = {}
    selectedCols.forEach(col => {
      newCheckboxes[col.slug_name] = checked
    })
    setSelectedCheckboxes(newCheckboxes)
  }

  const moveSelectedToRight = () => {
    const checkedItems = availableCols.filter(col => availableCheckboxes[col.slug_name])
    const uncheckedItems = availableCols.filter(col => !availableCheckboxes[col.slug_name])

    setSelectedCols(prev => [...prev, ...checkedItems])
    setAvailableCols(uncheckedItems)
    setAvailableCheckboxes({})
  }

  const moveSelectedToLeft = () => {
    const checkedItems = selectedCols.filter(col => selectedCheckboxes[col.slug_name])
    const uncheckedItems = selectedCols.filter(col => !selectedCheckboxes[col.slug_name])

    setAvailableCols(prev => [...prev, ...checkedItems])
    setSelectedCols(uncheckedItems)
    setSelectedCheckboxes({})
  }

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Moving inside same list
    if (source.droppableId === destination.droppableId) {
      const list = source.droppableId === "available" ? [...availableCols] : [...selectedCols];
      const [moved] = list.splice(source.index, 1);
      list.splice(destination.index, 0, moved);

      if (source.droppableId === "available") setAvailableCols(list);
      else setSelectedCols(list);
    }
    // Moving between lists
    else {
      let sourceList = source.droppableId === "available" ? [...availableCols] : [...selectedCols];
      let destList = destination.droppableId === "available" ? [...availableCols] : [...selectedCols];

      const [moved] = sourceList.splice(source.index, 1);
      destList.splice(destination.index, 0, moved);

      setAvailableCols(source.droppableId === "available" ? sourceList : destList);
      setSelectedCols(source.droppableId === "selected" ? sourceList : destList);
    }
  }
  // Queue the export and let the worker email it.
  //
  // This used to POST /api/candidates/downloadcandidate with
  // responseType:'blob' and wait for the file inline. That endpoint builds a
  // styled xlsx of every ingested candidate — 13,680 rows, ~44s on a 53-column
  // selection and longer on a wider one — inside the web process, behind
  // nginx's default 60s proxy_read_timeout. It could not finish, so the
  // download failed every time.
  const handleDownload = async () => {
    if (!selectedCols.length) {
      message.warning('Select at least one column')
      return
    }

    try {
      setModalDownloadLoading(true)

      const resp = await candidateRequest.post('/api/candidates/download/async', {
        columns: selectedCols.map(c => c.slug_name),
        user_id: currentUserId,
        notify_email: currentUserEmail || null,
      })

      const { job_id: jobId } = resp.data
      setExportStatus({
        jobId,
        status: 'pending',
        requestedAt: new Date().toISOString(),
        notified: false,
        notifyEmail: currentUserEmail || null,
        errorMessage: null,
      })
      startPolling(jobId)

      setDownloadModal(false)
      setSelectedCols([])
      setAvailableCols([])
    } catch (e) {
      message.error('Could not start the export. Please try again.')
    } finally {
      // ✅ unlock everything AFTER completion
      setModalDownloadLoading(false)
      setDownloadLoading(false)
    }
  }

  const filterMenu = <div />

  const columns = [

    {
      title: 'CANDIDATE',
      dataIndex: 'name',
      key: 'name',
      width: '22%',
      render: (_, row) => (
        <div>
          <RowNumberData style={{ fontWeight: "bold", fontSize: "14px" }}>
            {row?.name || '-'}
          </RowNumberData>
          {row?.email && (
            <RowNumberEmailData style={{ fontSize: "12px", color: "#888" }}>
              {row.email}
            </RowNumberEmailData>
          )}
        </div>
      ),
    },
    // {
    //   title: 'ROLE',
    //   dataIndex: 'applied_role',
    //   key: 'applied_role',
    //   width: '14%',
    //   render: (_, row) => <RowNumberData>{row?.applied_role || row?.role || '-'}</RowNumberData>,
    // },
    {
      title: 'EXP',
      dataIndex: 'experience_years',
      key: 'experience_years',
      width: '8%',
      render: (_, row) => (
        <RowNumberData>
          {row?.experience_years ?? row?.experience ?? '-'}
          {row?.experience_years != null || row?.experience != null ? ' yrs' : ''}
        </RowNumberData>
      ),
    },
    {
      title: 'KEY SKILLS',
      dataIndex: 'key_skills',
      key: 'key_skills',
      width: '16%',
      render: (_, row) => {
        if (!row?.key_skills) {
          return <RowNumberData>-</RowNumberData>;
        }

        const skills = row.key_skills.split(",").map(s => s.trim());
        const firstTwo = skills.slice(0, 1).join(", ");
        const remaining = skills.length - 1;

        return (
          <RowNumberData title={row.key_skills}>
            {remaining > 0 ? (
              <>
                {firstTwo}{" "}
                <span style={{ color: "#3249D7", fontWeight: 500 }}>
                  +{remaining} more
                </span>
              </>
            ) : (
              firstTwo
            )}
          </RowNumberData>
        );
      },
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
      width: '10%',
      render: (_, row) => <RowNumberData>{row?.location || '-'}</RowNumberData>,
    },
    // {
    //   title: 'EDUCATION',
    //   dataIndex: 'education',
    //   key: 'education',
    //   width: '12%',
    //   render: (_, row) => (
    //     <div>
    //       <RowNumberData>{row?.education || '-'}</RowNumberData>
    //     </div>
    //   ),
    // },
    // {
    //   title: 'NOTICE',
    //   dataIndex: 'notice_period',
    //   key: 'notice_period',
    //   width: '12%',
    //   render: (_, row) => (
    //     <div>
    //       <RowNumberData>{row?.notice_period || '-'}</RowNumberData>
    //     </div>
    //   ),
    // },

    // {
    //   title: 'DATA STATUS',
    //   dataIndex: 'normalized_data',
    //   key: 'data_quality',
    //   width: 120,
    //   align: 'center',
    //   render: (_, row) => {
    //     const hasInvalidData = Array.isArray(row?.normalized_data) &&
    //       row.normalized_data.some(item => item.is_valid === false)

    //     if (!hasInvalidData) return (
    //       <Tag color="success" style={{ fontSize: 11 }}>Valid</Tag>
    //     )

    //     return (
    //       <Tag color="error" style={{ fontSize: 11 }} title="This candidate has invalid normalized fields">
    //         ⚠ Invalid
    //       </Tag>
    //     )
    //   },
    // },

    {
    title: 'DATA STATUS',
    dataIndex: 'valid_status',
    key: 'data_status',
    width: 120,
    align: 'center',
    render: (valid_status) => {
      if (valid_status === true) {
        return <Tag color="success" style={{ fontSize: 11 }}>Valid</Tag>
      }

      return <Tag color="error" style={{ fontSize: 11 }}>Invalid</Tag>
    },
  },

    // {
    //   title: 'API STATUS',
    //   dataIndex: 'api_sync_status',
    //   key: 'api_sync_status',
    //   width: 130,
    //   align: 'center',
    //   render: (_, row) => {
    //     // treat as failed if response has data even if status is yet_to_sync
    //     const isEffectivelyFailed = 
    //       row?.api_sync_status === 'failed' || 
    //       !!row?.create_full_student_response

    //     if (isEffectivelyFailed) {
    //       const tooltipContent = (
    //         <div style={{ fontSize: 11, maxWidth: 320 }}>
    //           <div><b>Error:</b> {row?.api_sync_message || row?.create_full_student_response || 'API call failed'}</div>
    //           {row?.create_full_student_response && (
    //             <div style={{ marginTop: 6 }}>
    //               <b>Response:</b>
    //               <pre style={{ fontSize: 10, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
    //                 {row.create_full_student_response}
    //               </pre>
    //             </div>
    //           )}
    //         </div>
    //       )
    //       return (
    //         <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: 360 }}>
    //           <Tag color="error" style={{ fontSize: 11, cursor: 'pointer' }}>
    //             ✕ API Failed
    //           </Tag>
    //         </Tooltip>
    //       )
    //     }

    //     if (row?.api_sync_status === 'success') {
    //       return (
    //         <Tooltip
    //           title={
    //             row?.create_full_student_response
    //               ? <pre style={{ fontSize: 10, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
    //                   {row.create_full_student_response}
    //                 </pre>
    //               : 'Synced successfully'
    //           }
    //           overlayStyle={{ maxWidth: 360 }}
    //         >
    //           <Tag color="success" style={{ fontSize: 11, cursor: 'pointer' }}>
    //             ✓ Synced
    //           </Tag>
    //         </Tooltip>
    //       )
    //     }

    //     if (row?.student_id === null && row?.api_sync_status === 'failed') {
    //       return (
    //           <Tag color="success" style={{ fontSize: 11, cursor: 'pointer' }}>
    //             yet to sync
    //           </Tag>
            
    //       )
    //     }

    //     if (row?.student_id) {
    //       return (
    //           <Tag color="success" style={{ fontSize: 11, cursor: 'pointer' }}>
    //             Synced
    //           </Tag>
            
    //       )
    //     }

    //     return (

    //       <Tag color="default" style={{ fontSize: 11 }}>
    //         Yet to Sync
    //       </Tag>
    //     )
    //   },
    // },

    {
  title: 'API STATUS',
  dataIndex: 'api_status',
  key: 'api_status',
  width: 130,
  align: 'center',
  render: (api_status, row) => {

    if (api_status === 'api_failed') {
      return (
        <Tooltip title={row?.api_sync_message || 'API Failed'}>
          <Tag color="error" style={{ fontSize: 11, cursor: 'pointer' }}>
            ✕ API Failed
          </Tag>
        </Tooltip>
      )
    }

    if (api_status === 'synced') {
      return (
        <Tag color="success" style={{ fontSize: 11 }}>
          ✓ Synced
        </Tag>
      )
    }

    if (api_status === 'not_synced') {
      return (
        <Tag color="default" style={{ fontSize: 11 }}>
          Not Synced
        </Tag>
      )
    }

    // fallback safety
    return (
      <Tag color="warning" style={{ fontSize: 11 }}>
        Unknown
      </Tag>
    )
  },
},

    {
      title: 'Action',
      dataIndex: '_id',
      align: 'center',
      width: 120,

      render: (_, row) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title="View">
            <EyeOutlined
              style={{ fontSize: 18, color: '#3249D7', cursor: 'pointer' }}
              // onClick={async () => {
              //   setSelectedCandidate(null)
              //   setCvValidationLog(null)
              //   setNormalizationLogs([])
              //   setDrawerLoading(true)
              //   setViewCandidateDrawer(true)
              //   try {
              //     const res = await candidateRequest.get(`/api/candidates/detail/${row.id}`)
              //     const detail = res?.data?.data
              //     if (detail) {
              //       setSelectedCandidate(detail)
              //       setNormalizationLogs(detail.normalization_logs || [])
              //       setCvValidationLog(detail.cv_validation_log || null)
              //     }
              //   } catch { } finally { setDrawerLoading(false) }
              // }}
              onClick={async () => {
                setSelectedCandidate(null)
                setCvValidationLog(null)
                setNormalizationLogs([])
                setDrawerLoading(true)
                setViewCandidateDrawer(true)
                try {
                  const res = await candidateRequest.get(`/api/candidates/detail/${row.id}`)
                  const detail = res?.data?.data
                  if (detail) {
                    setSelectedCandidate(detail)
                    setNormalizationLogs(detail.normalization_logs || [])
                    setCvValidationLog(detail.cv_validation_log || null)
                  }
                } catch { } finally { setDrawerLoading(false) }
              }}
            />
          </Tooltip>
        </div>
      ),
      // render: (_, row) => (
      //   <Button.Secondary
      //     height={'32px'}
      //     text={'View'}
      //     onClick={() => {
      //       setSelectedCandidate(row)
      //       setCvValidationLog(null)
      //       setNormalizationLogs([])
      //       setViewCandidateDrawer(true)
      //       if (row?.raw_data_id) {
      //         candidateRequest.get(`/api/candidates/normalization-log/${row.raw_data_id}`)
      //           .then(r => setNormalizationLogs(r?.data?.data || []))
      //           .catch(() => { })
      //         candidateRequest.get(`/api/candidates/cv-validation-log/${row.raw_data_id}`)
      //           .then(r => setCvValidationLog(r?.data?.data || null))
      //           .catch(() => { })
      //       }
      //     }}
      //   />
      // ),
    },

  ]

  const onPageChange = (num) => {
    setPage(num - 1)
  }
  const [modalDownloadLoading, setModalDownloadLoading] = useState(false)
  const onResetFilters = () => {
    setSearchValue('')
    setFilters({
      dateRange: null,
      keywordSearchMode: 'profile',
      genders: [],
      experience: [0, 22],
      locations: [],
      degrees: [],
      departments: [],
      yearsOfPassing: [],
      marks: [0, 100],
      assessment: [0, 100],
      noticePeriods: [],
      statuses: [],
    })
    setPage(0)
  }

  // const refreshSelectedCandidate = async () => {
  //   try {
  //     if (!selectedCandidate?.id) return
  //     const res = await candidateRequest.get(`/api/candidates/${selectedCandidate.id}`)
  //     const fresh = res?.data?.data
  //     if (fresh) {
  //       setSelectedCandidate(fresh)
  //       // also update it in the table list
  //       setData(prev => prev.map(c => c.id === fresh.id ? { ...c, ...fresh } : c))
  //     }
  //   } catch (e) {
  //     // silently fail, table will still work
  //   }
  // }

  const selectedCandidateRef = React.useRef(null)
  selectedCandidateRef.current = selectedCandidate

  const refreshSelectedCandidate = async () => {
    try {
      const current = selectedCandidateRef.current
      if (!current?.id) return
      const res = await candidateRequest.get(`/api/candidates/detail/${current.id}`)
      const fresh = res?.data?.data
      if (fresh) {
        setData(prev => prev.map(c => c.id === fresh.candidate_job_details_id ? { ...c, ...fresh } : c))
        setSelectedCandidate(fresh)
        setNormalizationLogs(fresh.normalization_logs || [])
        setCvValidationLog(fresh.cv_validation_log || null)
      }
    } catch (e) {
      // silently fail
    } finally {
      fetchCandidates()
    }
  }

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (searchValue?.trim()) count += 1
    if (filters?.dateRange?.[0] || filters?.dateRange?.[1]) count += 1
    if ((filters?.genders || []).length) count += 1
    if (filters?.experience?.[0] !== 0 || filters?.experience?.[1] !== 22) count += 1
    if ((filters?.locations || []).length) count += 1
    if ((filters?.degrees || []).length) count += 1
    if ((filters?.departments || []).length) count += 1
    if ((filters?.yearsOfPassing || []).length) count += 1
    if (filters?.marks?.[0] !== 0 || filters?.marks?.[1] !== 100) count += 1
    if (filters?.assessment?.[0] !== 0 || filters?.assessment?.[1] !== 100) count += 1
    if ((filters?.noticePeriods || []).length) count += 1
    if ((filters?.statuses || []).length) count += 1
    return count
  }, [filters, searchValue])

  return (
    <SectionWrapper column>
      <TopHeader row spaceBetween alignCenter>
        <TopHeading flexStart>Mismatched Candidates List</TopHeading>
        <div style={{ display: 'flex', gap: 10 }}>
          {/* <Button.Primary
            height={'36px'}
            text={clearLoading ? 'Clearing...' : 'Clear Data'}
            onClick={handleClearAll}
            disabled={clearLoading}
            style={{
              background: '#3249D7',
              borderColor: '#3249D7',
              color: '#fff'
            }}
          /> */}
          {/* <Button.Primary
            height={'36px'}
            text={downloadLoading ? 'Loading...' : 'Download'}
            onClick={() => {
              setDownloadLoading(true)
              setDownloadModal(true)
            }}
            disabled={downloadLoading || loading}
          /> */}
          <Button.Primary
            height={'36px'}
            text={ingestLoading ? 'Ingesting...' : 'Ingest'}
            onClick={handleIngest}
            disabled={ingestLoading}
          />
          {/* <Button.Primary
            height={'36px'}
            text={normalizationLoading ? 'Normalizing...' : 'Normalize Data'}
            onClick={handleNormalize}
            disabled={normalizationLoading}
          /> */}
          <Button.Secondary
            height={'36px'}
            text={'Refresh'}
            onClick={handleRefresh}
            disabled={loading}
          />
        </div>
      </TopHeader>

      <ExportStatusPanel
        status={exportStatus}
        onDismiss={() => setExportStatus(null)}
      />

      <NavWrapper column>
        <div style={{ display: 'block', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <TableSearchFilter
            placeholder={'Search by name / email / mobile / Role'}
            menu={filterMenu}
            open={isMenuOpen}
            setOpen={(open) => {
              if (open) {
                setFilterDrawerOpen(true)
              }
              setIsMenuOpen(false)
            }}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setCurrentPage={setPage}
            defaultFirstPageNumber={0}
            currentCount={PAGE_SIZE * page + (tableRows?.length || 0)}
            totalCount={total || tableRows?.length || 0}
            onOpenChange={(open) => {
              if (open) {
                setFilterDrawerOpen(true)
              }
              setIsMenuOpen(false)
            }}
            withoutFilter={false}
          />
          {/* <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
            Showing {PAGE_SIZE * page + (tableRows?.length || 0)} of {total || tableRows?.length || 0}
          </div> */}
        </div>

        {loading ? (
          <EmptyWrapper center>
            <Spin />
          </EmptyWrapper>
        ) : tableRows.length === 0 ? (
          <EmptyWrapper center>
            <Empty description="No candidates found!" />
          </EmptyWrapper>
        ) : (
          <>
            <StyledTable
              columns={columns}
              dataSource={tableRows}
              rowKey={(r) => String(r?.id ?? Math.random())}
              pagination={false}
            />

            {total > 0 && (
              <Pagination
                onChange={onPageChange}
                totalPages={total}
                pageSize={PAGE_SIZE}
                current={page + 1}
              />
            )}
          </>
        )}
        <Drawer
          title="Candidate Details"
          open={viewCandidateDrawer}
          onClose={() => {
            if (isSaving) return
            setViewCandidateDrawer(false)
          }}
          width={850}
          maskClosable={!isSaving}
          closable={!isSaving}
          keyboard={!isSaving}
          bodyStyle={{ padding: '16px', background: '#fafbfc' }}
          destroyOnClose={true}
        >
          <CandidateDetailsView
            isLoading={drawerLoading}
            setIsSaving={setIsSaving}
            refreshCandidate={refreshSelectedCandidate}
            onPushSuccess={() => setViewCandidateDrawer(false)}
            onPushFailed={() => setViewCandidateDrawer(false)}
            candidateInfo={{
              name: selectedCandidate?.name,
              email: selectedCandidate?.email,
              mobile: selectedCandidate?.mobile,
              role: selectedCandidate?.role,
              id: selectedCandidate?.id,
              source_file: selectedCandidate?.source_file,
              api_sync_status: selectedCandidate?.api_sync_status,
              api_sync_message: selectedCandidate?.api_sync_message,
              create_full_student_request: selectedCandidate?.create_full_student_request,
              create_full_student_response: selectedCandidate?.create_full_student_response,
              api_status: selectedCandidate?.api_status,
            }}
            assessmentScores={{
              aptitude_score: selectedCandidate?.aptitude_score,
              behavior_score: selectedCandidate?.behavior_score,
              communication_score: selectedCandidate?.communication_score
            }}
            rawData={selectedCandidate?.raw_data}
            normalizedData={
              selectedCandidate?.normalized_data?.filter(
                item => item.key !== "highest_qualification"
              )
            }
            cvData={selectedCandidate?.cv_data}
            normalizedColumns={selectedCandidate?.normalized_columns}
            cvValidationLog={cvValidationLog}
            normalizationLogs={normalizationLogs}
          />

          {/* {selectedCandidate?.raw_data && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              padding: 16,
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ 
                marginBottom: 12, 
                fontSize: 14, 
                fontWeight: 600,
                color: '#1f2937'
              }}>Raw Data</h3>
              <pre
                style={{
                  margin: 0,
                  background: '#1e293b',
                  color: '#e2e8f0',
                  padding: 16,
                  borderRadius: 6,
                  overflow: 'auto',
                  maxHeight: '250px',
                  fontSize: 11,
                  lineHeight: 1.5,
                  border: '1px solid #334155'
                }}
              >
                {JSON.stringify(selectedCandidate?.raw_data || {}, null, 2)}
              </pre>
            </div>
          )} */}
        </Drawer>

        <Drawer
          title={'Filters'}
          open={filterDrawerOpen}
          placement={'right'}
          width={360}
          onClose={() => setFilterDrawerOpen(false)}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: 14, borderBottom: '1px solid #eef0f4' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <span>Filters</span>
                <Badge count={activeFilterCount} size={'small'} />
              </div>
              <Button.Secondary height={'32px'} text={'Reset'} onClick={onResetFilters} />
            </div>
          </div>

          <div style={{ padding: 14, maxHeight: 'calc(100vh - 210px)', overflow: 'auto' }}>
            <Collapse
              defaultActiveKey={['keywords']}
              expandIconPosition={'end'}
              bordered={false}
              ghost
            >
              <Collapse.Panel header={'KEYWORDS'} key={'keywords'}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
                  <Input
                    placeholder={'Search keywords...'}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    size={'middle'}
                  />
                  <Radio.Group
                    value={filters.keywordSearchMode}
                    onChange={(e) => setFilters((prev) => ({ ...prev, keywordSearchMode: e.target.value }))}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                  >
                    <Radio value={'profile'}>Search in profile</Radio>
                    <Radio value={'keySkills'}>Search in key skills only</Radio>
                  </Radio.Group>
                </div>
              </Collapse.Panel>

              <Collapse.Panel header={'DATE RANGE'} key={'date'}>
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  value={filters.dateRange}
                  onChange={(value) => setFilters((prev) => ({ ...prev, dateRange: value }))}
                  size={'middle'}
                />
              </Collapse.Panel>

              <Collapse.Panel header={'GENDER'} key={'gender'}>
                <div style={{ paddingTop: 6 }}>
                  <Checkbox.Group
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                    value={filters.genders}
                    onChange={(value) => setFilters((prev) => ({ ...prev, genders: value }))}
                    options={['Male', 'Female', 'Other']}
                  />
                </div>
              </Collapse.Panel>

              <Collapse.Panel header={'EXPERIENCE'} key={'experience'}>
                <div style={{ paddingTop: 6 }}>
                  <div style={{ marginBottom: 6, color: '#6b7280', fontSize: 12 }}>
                    {filters.experience?.[0]} yrs - {filters.experience?.[1]} yrs
                  </div>
                  <Slider
                    range
                    min={0}
                    max={22}
                    value={filters.experience}
                    onChange={(value) => setFilters((prev) => ({ ...prev, experience: value }))}
                  />
                </div>
              </Collapse.Panel>

              <Collapse.Panel header={'LOCATION'} key={'location'}>
                <Select
                  mode={'multiple'}
                  style={{ width: '100%' }}
                  placeholder={'Select location'}
                  value={filters.locations}
                  onChange={(value) => setFilters((prev) => ({ ...prev, locations: value }))}
                  size={'middle'}
                  options={[
                    { value: 'Pune', label: 'Pune' },
                    { value: 'Noida', label: 'Noida' },
                    { value: 'Mumbai', label: 'Mumbai' },
                    { value: 'Bangalore', label: 'Bangalore' },
                  ]}
                />
              </Collapse.Panel>

              <Collapse.Panel header={'EDUCATION - DEGREE'} key={'degree'}>
                <Select
                  mode={'multiple'}
                  style={{ width: '100%' }}
                  placeholder={'Select degree'}
                  value={filters.degrees}
                  onChange={(value) => setFilters((prev) => ({ ...prev, degrees: value }))}
                  size={'middle'}
                  options={[
                    { value: 'B.Tech', label: 'B.Tech' },
                    { value: 'M.Tech', label: 'M.Tech' },
                    { value: 'MBA', label: 'MBA' },
                    { value: 'B.Sc', label: 'B.Sc' },
                  ]}
                />
              </Collapse.Panel>

              <Collapse.Panel header={'EDUCATION - DEPARTMENT'} key={'department'}>
                <Select
                  mode={'multiple'}
                  style={{ width: '100%' }}
                  placeholder={'Select department'}
                  value={filters.departments}
                  onChange={(value) => setFilters((prev) => ({ ...prev, departments: value }))}
                  size={'middle'}
                  options={[
                    { value: 'CSE', label: 'CSE' },
                    { value: 'ECE', label: 'ECE' },
                    { value: 'IT', label: 'IT' },
                    { value: 'ME', label: 'ME' },
                  ]}
                />
              </Collapse.Panel>

              <Collapse.Panel header={'YEAR OF PASSING'} key={'year'}>
                <Select
                  mode={'multiple'}
                  style={{ width: '100%' }}
                  placeholder={'Select year'}
                  value={filters.yearsOfPassing}
                  onChange={(value) => setFilters((prev) => ({ ...prev, yearsOfPassing: value }))}
                  size={'middle'}
                  options={[
                    { value: '2026', label: '2026' },
                    { value: '2025', label: '2025' },
                    { value: '2024', label: '2024' },
                    { value: '2023', label: '2023' },
                  ]}
                />
              </Collapse.Panel>

              <Collapse.Panel header={'% OF MARKS'} key={'marks'}>
                <div style={{ paddingTop: 6 }}>
                  <div style={{ marginBottom: 6, color: '#6b7280', fontSize: 12 }}>
                    {filters.marks?.[0]}% - {filters.marks?.[1]}%
                  </div>
                  <Slider
                    range
                    min={0}
                    max={100}
                    value={filters.marks}
                    onChange={(value) => setFilters((prev) => ({ ...prev, marks: value }))}
                  />
                </div>
              </Collapse.Panel>

              <Collapse.Panel header={'ASSESSMENT SCORE'} key={'assessment'}>
                <div style={{ paddingTop: 6 }}>
                  <div style={{ marginBottom: 6, color: '#6b7280', fontSize: 12 }}>
                    {filters.assessment?.[0]} - {filters.assessment?.[1]}
                  </div>
                  <Slider
                    range
                    min={0}
                    max={100}
                    value={filters.assessment}
                    onChange={(value) => setFilters((prev) => ({ ...prev, assessment: value }))}
                  />
                </div>
              </Collapse.Panel>

              <Collapse.Panel header={'NOTICE'} key={'notice'}>
                <Select
                  mode={'multiple'}
                  style={{ width: '100%' }}
                  placeholder={'Select notice'}
                  value={filters.noticePeriods}
                  onChange={(value) => setFilters((prev) => ({ ...prev, noticePeriods: value }))}
                  size={'middle'}
                  options={[
                    { value: 'Immediate', label: 'Immediate' },
                    { value: 'Serving Notice Period', label: 'Serving Notice Period' },
                    { value: 'More than 3 Months', label: 'More than 3 Months' },
                  ]}
                />
              </Collapse.Panel>

              <Collapse.Panel header={'STATUS'} key={'status'}>
                <Select
                  mode={'multiple'}
                  style={{ width: '100%' }}
                  placeholder={'Select status'}
                  value={filters.statuses}
                  onChange={(value) => setFilters((prev) => ({ ...prev, statuses: value }))}
                  size={'middle'}
                  options={[
                    { value: 'Shortlisted', label: 'Shortlisted' },
                    { value: 'Rejected', label: 'Rejected' },
                    { value: 'Maybe', label: 'Maybe' },
                    { value: 'Pending', label: 'Pending' },
                  ]}
                />
              </Collapse.Panel>
            </Collapse>
          </div>

          <div
            style={{
              padding: 14,
              borderTop: '1px solid #eef0f4',
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
            }}
          >
            <Button.Secondary height={'32px'} text={'Reset'} onClick={onResetFilters} />
            <Button.Primary height={'32px'} text={'Apply'} onClick={() => setFilterDrawerOpen(false)} />
          </div>
        </Drawer>
        <Modal
          open={downloadModal}
          onCancel={() => {
            setDownloadModal(false)
            setSelectedCols([])
            setAvailableCols([])
            setAvailableCheckboxes({})
            setSelectedCheckboxes({})
            setModalDownloadLoading(false) // ✅ correct
            setDownloadLoading(false)

          }}
          width={800}
          title={`Download (${selectedCols.length} selected)`}
          bodyStyle={{
            maxHeight: "500px",
            overflow: "hidden",
          }}
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
              }}
            >
              <Button.Secondary
                key="cancel"
                text="Cancel"
                onClick={() => {
                  setDownloadModal(false)

                  setSelectedCols([])
                  setAvailableCols([])
                  setAvailableCheckboxes({})
                  setSelectedCheckboxes({})

                  setModalDownloadLoading(false)
                  setDownloadLoading(false) // ✅ FIX
                }}
                disabled={modalDownloadLoading}
              />
              <Button.Primary
                key="download"
                text={modalDownloadLoading ? "Downloading..." : "Download"}
                onClick={handleDownload}
                disabled={modalDownloadLoading}
              />
            </div>
          }
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <div style={{ display: "flex", gap: 20 }}>

              {/* AVAILABLE */}
              <Droppable droppableId="available">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      flex: 1,
                      height: 300,
                      overflowY: "auto",
                      border: "1px solid #eee",
                      padding: 10
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <h4 style={{ margin: 0 }}>Available Columns</h4>
                      <Checkbox
                        checked={availableCols.length > 0 && Object.keys(availableCheckboxes).length === availableCols.length && Object.values(availableCheckboxes).every(Boolean)}
                        indeterminate={Object.keys(availableCheckboxes).length > 0 && Object.values(availableCheckboxes).some(Boolean) && !Object.values(availableCheckboxes).every(Boolean)}
                        onChange={(e) => handleSelectAllAvailable(e.target.checked)}
                      >
                        Select All
                      </Checkbox>
                    </div>

                    {availableCols.map((col, index) => (
                      <Draggable key={col.slug_name} draggableId={col.slug_name} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              padding: "8px 10px",
                              marginBottom: 8,
                              background: "#fafafa",
                              border: "1px solid #ddd",
                              borderRadius: 6,
                              cursor: "grab",
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              ...provided.draggableProps.style
                            }}
                          >
                            <Checkbox
                              checked={availableCheckboxes[col.slug_name] || false}
                              onChange={(e) => handleAvailableCheckboxChange(col.slug_name, e.target.checked)}
                            />
                            {col.display_name}
                          </div>
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {/* ARROW BUTTONS */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10 }}>
                <AntButton
                  onClick={moveSelectedToRight}
                  disabled={Object.values(availableCheckboxes).filter(Boolean).length === 0}
                  title="Move selected to right"
                  style={{
                    width: 40,
                    height: 40,
                    borderColor: '#3249D7',
                    color: '#3249D7',
                    backgroundColor: Object.values(availableCheckboxes).filter(Boolean).length === 0 ? '#ccc' : '#fff',
                  }}
                  icon={<RightOutlined />}
                />

                <AntButton
                  onClick={moveSelectedToLeft}
                  disabled={Object.values(selectedCheckboxes).filter(Boolean).length === 0}
                  title="Move selected to left"
                  style={{
                    width: 40,
                    height: 40,
                    borderColor: '#3249D7',
                    color: '#3249D7',
                    backgroundColor: Object.values(selectedCheckboxes).filter(Boolean).length === 0 ? '#ccc' : '#fff',
                  }}
                  icon={<LeftOutlined />}
                />
              </div>

              {/* SELECTED */}
              <Droppable droppableId="selected">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      flex: 1,
                      height: 300,
                      overflowY: "auto",
                      border: "1px solid #eee",
                      padding: 10,
                      background: "#f0f7ff"
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <h4 style={{ margin: 0 }}>Selected Columns </h4>
                      <Checkbox
                        checked={selectedCols.length > 0 && Object.keys(selectedCheckboxes).length === selectedCols.length && Object.values(selectedCheckboxes).every(Boolean)}
                        indeterminate={Object.keys(selectedCheckboxes).length > 0 && Object.values(selectedCheckboxes).some(Boolean) && !Object.values(selectedCheckboxes).every(Boolean)}
                        onChange={(e) => handleSelectAllSelected(e.target.checked)}
                      >
                        Select All
                      </Checkbox>
                    </div>

                    {selectedCols.map((col, index) => (
                      <Draggable key={col.slug_name} draggableId={col.slug_name} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              padding: "8px 10px",
                              marginBottom: 8,
                              background: "#d6e4ff",
                              border: "1px solid #91caff",
                              borderRadius: 6,
                              cursor: "grab",
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              ...provided.draggableProps.style
                            }}
                          >
                            <Checkbox
                              checked={selectedCheckboxes[col.slug_name] || false}
                              onChange={(e) => handleSelectedCheckboxChange(col.slug_name, e.target.checked)}
                            />
                            {col.display_name}
                          </div>
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

            </div>
          </DragDropContext>
        </Modal>
      </NavWrapper>
    </SectionWrapper>
  )
}

export default CandidateMetrics